import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, RefreshCw, RotateCcw, CheckCircle } from "lucide-react";
import { ALL_SECTIONS } from "./constants";

const MONTH_OPTIONS = [
  ["Apr", "April"], ["May", "May"], ["Jun", "June"], ["Jul", "July"],
  ["Aug", "August"], ["Sep", "September"], ["Oct", "October"], ["Nov", "November"],
  ["Dec", "December"], ["Jan", "January"], ["Feb", "February"], ["Mar", "March"],
];

const QUARTER_OPTIONS = [
  ["Q1", "Q1 (Apr-Jun)"], ["Q2", "Q2 (Jul-Sep)"],
  ["Q3", "Q3 (Oct-Dec)"], ["Q4", "Q4 (Jan-Mar)"],
];

export const TransactionFilters = ({
  search, setSearch,
  periods, selectedFY, setSelectedFY,
  periodType, setPeriodType,
  selectedMonth, setSelectedMonth,
  selectedQuarter, setSelectedQuarter,
  customStartDate, setCustomStartDate,
  customEndDate, setCustomEndDate,
  sectionFilter, setSectionFilter,
  showSectionDropdown, setShowSectionDropdown,
  showExcluded, setShowExcluded, excludedCount,
  showMismatchOnly, setShowMismatchOnly, mismatchCount,
  showNoPanOnly, setShowNoPanOnly, noPanCount,
  showNoPartyNameOnly, setShowNoPartyNameOnly, noPartyNameCount,
  availableSections, selectedSections, toggleSectionFilter, setSelectedSections,
  filteredTransactions,
  handleSearch, onReset, onBulkRestore, canEdit,
}) => (
  <Card className="mb-6">
    <CardContent className="p-4">
      <form onSubmit={handleSearch} className="flex gap-4 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs text-slate-500 mb-1">Search Party</Label>
          <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-party-input" />
        </div>

        <div className="w-36">
          <Label className="text-xs text-slate-500 mb-1">FY</Label>
          <Select value={selectedFY} onValueChange={setSelectedFY}>
            <SelectTrigger><SelectValue placeholder="All Years" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {periods.financial_years.map((fy) => (
                <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-32">
          <Label className="text-xs text-slate-500 mb-1">Period</Label>
          <Select
            value={periodType}
            onValueChange={(v) => {
              setPeriodType(v);
              setSelectedQuarter("");
              setSelectedMonth("");
              setCustomStartDate("");
              setCustomEndDate("");
            }}
          >
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {periodType === "month" && (
          <div className="w-40">
            <Label className="text-xs text-slate-500 mb-1">Month</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger><SelectValue placeholder="Select Month" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {MONTH_OPTIONS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {periodType === "quarter" && (
          <div className="w-36">
            <Label className="text-xs text-slate-500 mb-1">Quarter</Label>
            <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
              <SelectTrigger><SelectValue placeholder="All Qtrs" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Quarters</SelectItem>
                {QUARTER_OPTIONS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {periodType === "custom" && (
          <>
            <div className="w-36">
              <Label className="text-xs text-slate-500 mb-1">From</Label>
              <Input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
            </div>
            <div className="w-36">
              <Label className="text-xs text-slate-500 mb-1">To</Label>
              <Input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
            </div>
          </>
        )}

        <div className="w-44 relative">
          <Label className="text-xs text-slate-500 mb-1">Section</Label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowSectionDropdown(!showSectionDropdown)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md bg-white hover:border-blue-400 transition-colors"
              data-testid="section-dropdown-trigger"
            >
              <span className={sectionFilter && sectionFilter !== "all" ? "text-slate-900" : "text-slate-500"}>
                {sectionFilter && sectionFilter !== "all" ? sectionFilter : "All"}
              </span>
              <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showSectionDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                <div
                  className="px-3 py-2 text-sm hover:bg-slate-100 cursor-pointer"
                  onClick={() => { setSectionFilter("all"); setShowSectionDropdown(false); }}
                >
                  All
                </div>
                {ALL_SECTIONS.map((s) => (
                  <div
                    key={s.section}
                    className="px-3 py-2 text-sm hover:bg-slate-100 cursor-pointer flex items-center justify-between"
                    onClick={() => { setSectionFilter(s.section); setShowSectionDropdown(false); }}
                  >
                    <span>{s.section} - {s.description}</span>
                    {sectionFilter === s.section && <CheckCircle className="w-4 h-4 text-blue-600" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <Button type="submit" data-testid="filter-apply-btn"><Filter className="w-4 h-4 mr-2" />Filter</Button>
        <Button type="button" variant="outline" onClick={onReset} data-testid="filter-reset-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </form>

      <div className="flex gap-6 mt-4 pt-4 border-t flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={showExcluded} onCheckedChange={setShowExcluded} data-testid="filter-show-excluded" />
          <span className="text-sm">Show Excluded ({excludedCount})</span>
        </label>
        {canEdit && showExcluded && excludedCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="text-green-600 border-green-300 h-7"
            onClick={onBulkRestore}
            data-testid="bulk-restore-btn"
          >
            <RotateCcw className="w-3 h-3 mr-1" />Restore All ({excludedCount})
          </Button>
        )}
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={showMismatchOnly} onCheckedChange={setShowMismatchOnly} />
          <span className="text-sm text-amber-600 font-medium">Show Mismatches Only ({mismatchCount})</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={showNoPanOnly} onCheckedChange={setShowNoPanOnly} data-testid="filter-no-pan" />
          <span className="text-sm text-red-600 font-medium">No PAN Only ({noPanCount})</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox checked={showNoPartyNameOnly} onCheckedChange={setShowNoPartyNameOnly} data-testid="filter-no-party" />
          <span className="text-sm text-orange-600 font-medium">No Party Name ({noPartyNameCount})</span>
        </label>
      </div>

      {availableSections.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <Label className="text-xs text-slate-500 mb-2 block">Filter by Sections (select multiple)</Label>
          <div className="flex gap-2 flex-wrap">
            {availableSections.map((section) => (
              <button
                key={section}
                onClick={() => toggleSectionFilter(section)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                  selectedSections.includes(section)
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"
                }`}
                data-testid={`section-filter-${section}`}
              >
                {section}
              </button>
            ))}
            {selectedSections.length > 0 && (
              <button
                onClick={() => setSelectedSections([])}
                className="px-3 py-1.5 text-sm rounded-full border border-red-300 text-red-600 hover:bg-red-50"
              >
                Clear All
              </button>
            )}
          </div>
          {selectedSections.length > 0 && (
            <p className="text-xs text-blue-600 mt-2">
              Showing: {selectedSections.join(", ")} ({filteredTransactions.length} transactions)
            </p>
          )}
        </div>
      )}
    </CardContent>
  </Card>
);
